import { useState } from 'react'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { sileo } from 'sileo'
import { FacturaAPI } from '../../../../../../../api/facturas.api'
// import { getErrorMessage } from '../../utils/utils'

export default function FirmaNotaCredito({
  document,
  baseDoc,
  currentBusiness,
  onBack,
  onEmitSuccess
}) {
  const [accepted, setAccepted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  // true solo durante el tramo entre "ya se envió al SII" y "ya tenemos el resultado final" --
  // es el hueco que hoy no tiene ningún mensaje visible más que el toast que se envió.
  const [isValidating, setIsValidating] = useState(false)
  const api = new FacturaAPI()

  const formatCLP = v =>
    Number(v || 0).toLocaleString('es-CL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })

  const delay = ms => new Promise(res => setTimeout(res, ms))

  // Pollea el estado ya emitido. Un error acá (red, CORS, etc.) NO significa que la
  // emisión haya fallado -- la emisión ya terminó antes de llegar a esta función.
  const pollDocumentoEstado = async documentId => {
    const POLL_SUCCESS_STATUSES = ['issued', 'delivered', 'paid', 'unpaid']
    const POLL_ERROR_STATUSES = ['rejected', 'cancelled', 'issue_failed', 'invalid', 'permanent_error']
    const POLL_INTERVAL_MS = 4000
    const POLL_MAX_ATTEMPTS = 30 // ~2 minutos

    for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
      await delay(POLL_INTERVAL_MS)
      try {
        const statusRes = await api.getEstadoLocal(documentId)
        const status = statusRes.data?.status

        if (POLL_SUCCESS_STATUSES.includes(status)) return { type: 'issued' }
        if (POLL_ERROR_STATUSES.includes(status)) return { type: 'rejected' }
      } catch (err) {
        console.error('Error consultando estado-local (la Nota de Crédito ya fue emitida):', err)
        // seguimos intentando; no cortamos el polling por un error puntual de red/CORS
      }
    }
    return { type: 'unknown' }
  }

  const handleEmitClick = async () => {
    if (!accepted || isSubmitting) return

    setIsSubmitting(true)
    try {
      // Limpiamos el objeto dte_type para que solo lleve el code, evitando enviar description
      // que no está en el contrato del backend.
      const cleanedDocument = {
        ...document,
        dte_type: { code: document.dte_type.code }
      }

      const payload = { documents: [cleanedDocument] }
      const xUseDefaults = true // Valor por defecto usado en facturas

      let documentId
      try {
        const res = await sileo.promise(api.emitirFactura(currentBusiness.id, xUseDefaults, payload), {
          loading: { title: 'Enviando la Nota de Crédito al SII...' },
          success: { title: 'Aviso', description: 'Documento enviado, validando con el SII...' },
          error: { title: 'Error de Emisión', description: 'No se pudo enviar la Nota de Crédito al SII 🤯' }
        })
        documentId = res.data?.response_tupana?.documents?.[0]?.document?.id
        if (!documentId) throw new Error('No se pudo obtener el ID del documento en Tupana')
      } catch (emitError) {
        console.error('Error al emitir la Nota de Crédito:', emitError)
        return
      }

      // A partir de acá la emisión ya está confirmada; lo que sigue es solo
      // seguimiento de estado, nunca debe reportarse como "falló la emisión".
      setIsValidating(true)
      const outcome = await pollDocumentoEstado(documentId)
      setIsValidating(false)

      if (outcome.type === 'issued') {
        sileo.success({
          title: 'Emisión Exitosa',
          description: (
            <div className='flex flex-col gap-1'>
              <span className='text-green-500/80 font-medium'>
                ¡Nota de Crédito emitida correctamente! 👌
              </span>
            </div>
          )
        })
        if (onEmitSuccess) onEmitSuccess()
      } else if (outcome.type === 'rejected') {
        sileo.error({
          title: 'Documento Rechazado',
          description: 'La Nota de Crédito fue rechazada por el SII.'
        })
        if (onEmitSuccess) onEmitSuccess()
      } else {
        sileo.warning({
          title: 'Enviada, confirmación pendiente',
          description:
            'La Nota de Crédito se envió correctamente al SII. Todavía no pudimos confirmar el estado final -- revisa el listado en unos minutos.'
        })
        if (onEmitSuccess) onEmitSuccess()
      }
    } finally {
      setIsSubmitting(false)
      setIsValidating(false)
    }
  }

  // document.details[0].unit_price es neto (sin IVA); se recompone el total a mostrar
  // igual que en NotaCreditoPreviewPDF, según si el documento referenciado es exento (34).
  const netAmount = Number(document?.details?.[0]?.unit_price || 0)
  const isExenta = document?.references?.[0]?.dte_type_code === '34'
  const totalAmount = isExenta ? netAmount : netAmount + Math.round(netAmount * 0.19)

  return (
    <div className='space-y-6'>
      <header className='flex items-center justify-between'>
        <button
          onClick={onBack}
          className='flex items-center gap-2 text-[#000033] hover:underline font-semibold'
        >
          <ArrowLeft size={18} /> Volver a la revisión
        </button>
      </header>

      <section className='bg-white rounded-2xl shadow p-6 space-y-4'>
        <h1 className='text-2xl font-semibold text-center mb-2'>
          Firma y Emisión de Nota de Crédito
        </h1>
        <p className='text-sm text-gray-600 text-center'>
          Este es el paso final para anular el documento. Revisa el resumen y firma con tu
          certificado digital para informar al SII.
        </p>

        <div className='bg-gray-50 rounded-xl p-4 mt-4'>
          <h2 className='text-base font-semibold mb-3'>Resumen de Anulación</h2>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4 text-sm'>
            <div className='border-r border-gray-200 pr-4'>
              <div className='text-gray-500 font-medium uppercase text-[10px] mb-1'>
                Documento a Anular
              </div>
              <div className='font-bold text-gray-800'>
                {baseDoc.dte_type_description || 'Documento'} N° {baseDoc.folio}
              </div>
              <div className='text-gray-600 text-xs mt-1'>Emitido el: {baseDoc.date_issued}</div>
            </div>
            <div className='pl-2'>
              <div className='text-gray-500 font-medium uppercase text-[10px] mb-1'>
                Total Nota de Crédito
              </div>
              <div className='font-bold text-xl text-[#000033]'>
                ${formatCLP(totalAmount)}
              </div>
            </div>
          </div>

          <div className='mt-4 border rounded-lg bg-white p-4'>
            <div className='text-xs text-gray-500 font-medium uppercase mb-2'>Receptor</div>
            <div className='flex items-start justify-between text-sm'>
              <div>
                <div className='font-semibold text-gray-800'>
                  {document.document_receiver.business_name}
                </div>
                <div className='text-gray-600 text-xs'>{document.document_receiver.rut}</div>
              </div>
            </div>
          </div>
        </div>

        <div className='bg-gray-50 rounded-xl p-4 space-y-3'>
          <div className='flex items-start gap-3 text-sm'>
            <input
              type='checkbox'
              id='accept-nc'
              className='mt-1 w-4 h-4 cursor-pointer'
              checked={accepted}
              onChange={e => setAccepted(e.target.checked)}
            />
            <label htmlFor='accept-nc' className='cursor-pointer text-gray-700 leading-relaxed'>
              Confirmo que he revisado el documento de anulación y autorizo su emisión al SII usando
              mi certificado digital. Esta acción es irreversible.
            </label>
          </div>

          {isValidating && (
            <div className='flex items-start gap-3 mt-4 p-4 rounded-lg bg-teal-50 border border-teal-100'>
              <Loader2 size={20} className='text-teal-500 animate-spin mt-0.5 shrink-0' />
              <div>
                <p className='text-sm font-semibold text-teal-700'>
                  Tu Nota de Crédito fue enviada al SII y está siendo validada...
                </p>
                <p className='text-xs text-teal-600 mt-0.5'>
                  Esto puede tardar unos minutos. No cierres esta pantalla.
                </p>
              </div>
            </div>
          )}

          <div className='flex justify-between mt-6 pt-2 border-t border-gray-200'>
            <button
              type='button'
              onClick={onBack}
              className='btn-detalle-sec'
              disabled={isSubmitting}
            >
              Cancelar
            </button>

            <button
              type='button'
              disabled={!accepted || isSubmitting}
              onClick={handleEmitClick}
              className={`btn-detalle-pri ${!accepted ? 'opacity-50' : ''}`}
            >
              Firmar
            </button>
          </div>
        </div>
      </section>

      <style>
        {`
          .btn-detalle-pri {
            padding: 0.6rem 2rem;
            border-radius: 8px;
            background: #000033;
            color: #ffffff;
            font-size: 0.9rem;
            font-weight: 600;
            border: none;
            cursor: pointer;
            transition: all 0.2s;
          }
          .btn-detalle-pri:not(:disabled):hover {
            background: #000055;
            box-shadow: 0 4px 6px rgba(0, 0, 51, 0.2);
          }
          .btn-detalle-pri:disabled {
            cursor: not-allowed;
          }
          
          .btn-detalle-sec {
            padding: 0.6rem 2rem;
            border-radius: 8px;
            background: #ffffff;
            color: #666666;
            font-size: 0.9rem;
            font-weight: 600;
            border: 1px solid #e5e7eb;
            cursor: pointer;
            transition: all 0.2s;
          }
          .btn-detalle-sec:hover {
            background: #f9fafb;
            color: #333333;
          }
        `}
      </style>
    </div>
  )
}
